import { SubscriptionTypeEnum } from "@finway-group/shared/lib/models"
import { Button, Form, Modal } from "antd"
import { Store } from "antd/lib/form/interface"
import axios from "axios"
import httpStatus from "http-status"
import React from "react"
import { useTranslation } from "react-i18next"
import { ExportForm } from "~Components/forms/export.form"
import { useLoggedInEmployeeProfile } from "~Shared/hooks/user.hooks"
import { FileService, NotificationService } from "~Shared/services"
import { DialogService } from "~Shared/services/dialog.service"
import { NotificationTypeEnum } from "~Shared/services/notification.service"
import { extractErrors } from "~Shared/utils/errorHandler.utils"
import { adjustFilterDataForSelectedExpenseKind, shouldAskToAdvanceStatusOnExport } from "~Shared/utils/expense.utils"
import { convertFilterObjectIntoQueryFormat } from "~Shared/utils/filter.utils"
import { encodeFilterString } from "~Shared/utils/helper.utils"
import { useStateIfMounted } from "~Shared/utils/hooks/useStateIfMounted"

interface ExportModalProps {
    isShowing: boolean
    subscriptions?: boolean
    transactions?: boolean
    onCancel: (state: boolean) => void
}
export const ExportModal = ({ isShowing, subscriptions = false, transactions = false, onCancel }: ExportModalProps) => {
    const { t } = useTranslation()
    const [formInstance] = Form.useForm()
    const loggedInUser = useLoggedInEmployeeProfile()

    const [isLoading, setIsLoading] = useStateIfMounted<boolean>(false)

    const exportExpenses = async (values: Store, shouldAdvanceStatus: boolean = false) => {
        const convertedValues = convertFilterObjectIntoQueryFormat(values, loggedInUser.settings.showGrossAmount)
        // prettier-ignore
        const archiveQueryString = values.includeArchive === true ? `` : `&isArchived[is]=false`
        const encodedQueryString = `${encodeFilterString(convertedValues)}&format=${values.format}&deleted[is]=false&subscriptionType[ne]=${SubscriptionTypeEnum.INITIAL_REQUEST}${archiveQueryString}`

        try {
            const response = await FileService.export(encodedQueryString, !shouldAdvanceStatus)

            const isAsyncExport = response?.status === httpStatus.ACCEPTED

            if (!isAsyncExport) {
                FileService.downloadFileAsSignedUrl(response?.data.fileUrl)
            }

            NotificationService.showNotificationOnExportSuccess(response)

            handleHide()
        } catch (err: unknown) {
            if (axios.isAxiosError(err) && err.response) {
                if (err.response.status === httpStatus.UNPROCESSABLE_ENTITY) {
                    NotificationService.send(NotificationTypeEnum.WARNING, t("error:file:export_no_data:title"), extractErrors(err.response)[0])
                } else if (err.response.status === httpStatus.NOT_FOUND) {
                    NotificationService.send(NotificationTypeEnum.WARNING, t("error:file:export_no_data:title"), t("error:file:export_no_data:message"))
                }
            } else {
                NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:file:export:title"))
            }
        } finally {
            setIsLoading(false)
        }
    }

    const exportTransactions = () => {
        NotificationService.send(NotificationTypeEnum.INFO, "Transaction export", "To be implemented")
        setIsLoading(false)
        onCancel(false)
    }

    const onSubmit = async (values: Store) => {
        let formValues = { ...values }

        if (formValues.minDate) {
            formValues.minDate.startOf("day")
        }
        if (formValues.maxDate) {
            formValues.maxDate.endOf("day")
        }
        if (formValues.minInvoiceDate) {
            formValues.minInvoiceDate.startOf("day")
        }
        if (formValues.maxInvoiceDate) {
            formValues.maxInvoiceDate.endOf("day")
        }

        formValues = adjustFilterDataForSelectedExpenseKind(formValues)

        let shouldAdvanceStatus

        if (shouldAskToAdvanceStatusOnExport(formValues.format, formValues.status)) {
            const { resolved, advanceStatus } = await DialogService.confirmExpenseStatusAdvancement()
            if (!resolved) {
                return
            }
            shouldAdvanceStatus = advanceStatus
        }

        setIsLoading(true)
        if (!transactions) {
            exportExpenses(formValues, shouldAdvanceStatus)
        } else {
            exportTransactions()
        }
    }

    const handleHide = () => {
        setIsLoading(false)
        formInstance.resetFields()
        onCancel(false)
    }

    return (
        <Modal
            open={isShowing}
            maskClosable={false}
            title={t("action:request.export.title")}
            onCancel={handleHide}
            className="ant-modal--small"
            footer={[
                <Button key="back" onClick={() => onCancel(false)}>
                    {t("action:cancel")}
                </Button>,
                <Button key="submit" type="primary" loading={isLoading} onClick={formInstance.submit}>
                    {t("action:request.export.submit")}
                </Button>,
            ]}
            destroyOnClose
            closable
            keyboard
        >
            <ExportForm subscriptions={subscriptions} transactions={transactions} formInstance={formInstance} onSubmit={onSubmit} />
        </Modal>
    )
}
